import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DrawerFlow, { DrawerFlowStep } from 'src/UI/drawer-flow/DrawerFlow';
import { Client } from 'src/users';
import ProgressSelectClientSvg from './assets/progress-select-client.svg?react';
import ProgressSelectTemplateSvg from './assets/progress-select-template.svg?react';
import ProgressSpecifyAppendicesSvg from './assets/progress-specify-appendices.svg?react';
import ProgressSpecifyDocumentsSvg from './assets/progress-specify-documents.svg?react';
import { useLastReviewDate } from './hooks/useLastReviewDate';
import SelectAppendicesStep from 'src/suitability-report/steps/SelectAppendicesStep';
import SelectDocumentsStep from 'src/suitability-report/steps/SelectDocumentsStep';
import SuitabilitySelectTemplateStep from 'src/suitability-report/steps/SuitabilitySelectTemplateStep';
import SelectSuitabilityReportClientStep from 'src/suitability-report/steps/SelectSuitabilityReportClientStep';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';

type SuitabilityFlowProps = {
  flowId: string;
  opened: boolean;
  close: () => void;
};

const steps: DrawerFlowStep<ReportData>[] = [
  {
    title: 'Select Client',
    showClientName: false,
    onNext: (data: ReportData, flowId?: string) => {
      Mixpanel.track(MixpanelEvents.SUITABILITY_REPORT_FLOW_CLIENT_SELECTED, { ...data, flowId });
    },
    screens: [
      {
        component: SelectSuitabilityReportClientStep,
        renderProgress: () => <ProgressSelectClientSvg />,
      },
    ],
  },
  {
    title: 'Select Template',
    clientNamePrefix: 'for',
    onNext: (data: ReportData, flowId?: string) => {
      Mixpanel.track(MixpanelEvents.SUITABILITY_REPORT_FLOW_TEMPLATE_SELECTED, { ...data, flowId });
    },
    screens: [
      {
        component: SuitabilitySelectTemplateStep,
        renderProgress: () => <ProgressSelectTemplateSvg />,
      },
    ],
  },
  {
    title: 'Select Documents',
    clientNamePrefix: 'for',
    screens: [
      {
        component: SelectDocumentsStep,
        renderProgress: () => <ProgressSpecifyDocumentsSvg />,
        onNext: (data: ReportData, flowId?: string) => {
          Mixpanel.track(MixpanelEvents.SUITABILITY_REPORT_FLOW_ADVICE_DOCUMENTS_SELECTED, {
            ...data,
            flowId,
          });
        },
      },
      {
        component: SelectAppendicesStep,
        renderProgress: () => <ProgressSpecifyAppendicesSvg />,
      },
    ],
  },
];

export type ReportData = {
  client?: Client;
  selectedTemplateId?: string;
  selectedDocumentIds: string[];
  selectedAppendixIds: number[];
  temporaryAppendixIds: number[];
  useDeepThinking: boolean;
  addTitlePage: boolean;
  addTableOfContents: boolean;
  enableLastReview: boolean;
  lastClientReview?: Date;
  summariseSupportingDocuments: boolean;
  flowId: string;
};

const initialData: ReportData = {
  client: undefined,
  selectedTemplateId: undefined,
  selectedDocumentIds: [],
  selectedAppendixIds: [],
  temporaryAppendixIds: [],
  useDeepThinking: false,
  addTitlePage: true,
  addTableOfContents: true,
  enableLastReview: true,
  lastClientReview: undefined,
  summariseSupportingDocuments: true,
  flowId: '',
};

export default function SuitabilityFlow({ opened, close, flowId }: SuitabilityFlowProps) {
  const [data, setData] = useState<ReportData>({ ...initialData, flowId });
  const navigate = useNavigate();
  const { lastReviewDate, refetch } = useLastReviewDate(data.client?.id);

  useEffect(() => {
    setData(prev => {
      return { ...prev, flowId };
    });
  }, [flowId]);

  useEffect(() => {
    if (opened && data.client?.id) {
      refetch();
    }
  }, [opened, data.client?.id]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (lastReviewDate && data.lastClientReview == null) {
      setData(prev => ({
        ...prev,
        lastClientReview: lastReviewDate,
        enableLastReview: true,
        flowId,
      }));
    } else if (!lastReviewDate) {
      setData(prev => ({ ...prev, enableLastReview: false, flowId }));
    }
  }, [opened, lastReviewDate, data.lastClientReview]);

  const onSuccess = useCallback(
    (data: ReportData) => {
      const clientId = data.client?.id;
      navigate(`/client/${clientId}/document/suitability-reports`);
    },
    [navigate],
  );

  return (
    <DrawerFlow
      opened={opened}
      close={close}
      title="Suitability report"
      steps={steps}
      initialData={initialData}
      data={data}
      setData={setData}
      onSuccess={onSuccess}
      flowId={flowId}
    />
  );
}
